import * as React from 'react'
import { Check, ChevronsUpDown, X, Search, Plus } from 'lucide-react'
import { cn } from '@frontend/lib/utils'
import { Button } from '@frontend/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/components/ui/popover'

export interface MultiSelectOption {
  label: string
  value: string
  isManual?: boolean
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  /** Allow creating new items from the search input */
  allowCreate?: boolean
  /** Callback when a new item is created (returns the label) */
  onCreateItem?: (label: string) => void
  /** Items that were manually created (shown with different styling) */
  manualItems?: string[]
  /** Callback to remove a manual item */
  onRemoveManualItem?: (label: string) => void
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found.',
  className,
  disabled = false,
  allowCreate = false,
  onCreateItem,
  manualItems = [],
  onRemoveManualItem,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const filteredOptions = React.useMemo(() => {
    let result = options
    if (search) {
      const lowerSearch = search.toLowerCase()
      result = options.filter(
        (option) => option.label.toLowerCase().includes(lowerSearch) || option.value.toLowerCase().includes(lowerSearch)
      )
    }
    // Sort alphabetically by label
    return [...result].sort((a, b) => a.label.localeCompare(b.label))
  }, [options, search])

  // Check if search term matches an existing option or manual item
  const canCreate = React.useMemo(() => {
    if (!allowCreate || !search.trim()) return false
    const lowerSearch = search.trim().toLowerCase()
    const existsInOptions = options.some((o) => o.label.toLowerCase() === lowerSearch)
    const existsInManual = manualItems.some((m) => m.toLowerCase() === lowerSearch)
    return !existsInOptions && !existsInManual
  }, [allowCreate, search, options, manualItems])

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const handleRemove = (value: string, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    onChange(selected.filter((v) => v !== value))
  }

  const handleRemoveManual = (label: string, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    onRemoveManualItem?.(label)
  }

  const handleCreate = () => {
    const trimmed = search.trim()
    if (trimmed && canCreate) {
      onCreateItem?.(trimmed)
      setSearch('')
    }
  }

  const selectedLabels = selected.map((value) => options.find((o) => o.value === value)?.label).filter(Boolean)

  const hasSelections = selected.length > 0 || manualItems.length > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal min-h-10 h-auto',
            !hasSelections && 'text-muted-foreground',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1 text-left">
            {!hasSelections ? (
              <span>{placeholder}</span>
            ) : (
              <>
                {/* Selected options from the list */}
                {selectedLabels.map((label, i) => (
                  <span
                    key={selected[i]}
                    className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground rounded px-2 py-0.5 text-xs"
                  >
                    {label}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleRemove(selected[i], e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleRemove(selected[i], e)
                        }
                      }}
                      className="hover:text-destructive focus:outline-none cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </span>
                ))}
                {/* Manually created items */}
                {manualItems.map((label) => (
                  <span
                    key={`manual-${label}`}
                    className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded px-2 py-0.5 text-xs"
                  >
                    <Plus className="h-3 w-3" />
                    {label}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleRemoveManual(label, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleRemoveManual(label, e)
                        }
                      }}
                      className="hover:text-destructive focus:outline-none cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </span>
                ))}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        {/* Search input */}
        <div className="flex items-center border-b border-border px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault()
                handleCreate()
              }
            }}
            placeholder={searchPlaceholder}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="opacity-50 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Options list */}
        <div
          className="max-h-60 overflow-y-auto p-1 overscroll-contain"
          onWheel={(e) => {
            // Prevent the wheel event from being captured by the popover
            e.stopPropagation()
          }}
        >
          {/* Create new option */}
          {canCreate && (
            <button
              type="button"
              onClick={handleCreate}
              className={cn(
                'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                'hover:bg-accent hover:text-accent-foreground',
                'focus:bg-accent focus:text-accent-foreground',
                'text-primary'
              )}
            >
              <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
                <Plus className="h-4 w-4" />
              </span>
              Create "{search.trim()}"
            </button>
          )}

          {filteredOptions.length === 0 && !canCreate ? (
            <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selected.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggle(option.value)}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus:bg-accent focus:text-accent-foreground'
                  )}
                >
                  <span
                    className={cn(
                      'absolute left-2 flex h-4 w-4 items-center justify-center',
                      isSelected ? 'opacity-100' : 'opacity-0'
                    )}
                  >
                    <Check className="h-4 w-4" />
                  </span>
                  {option.label}
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
